import { useCallback, useEffect, useMemo, useState } from "react";

import { Audit } from "../../../api";

import styles from "./AuditPage.module.scss";
import {
  LIMIT_OPTIONS,
  SESSION_EVENT_OPTIONS,
  describeIp,
  describeUserAgent,
  formatDateTime,
  getSessionBadgeClass,
  getSessionEventLabel,
  getSessionUserName,
  getSessionsResponse,
} from "./AuditPage.form";

export function SessionsPanel() {
  const [sessions, setSessions] = useState([]);

  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 1,
    page: 1,
    limit: 20,
  });

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");

  const [limit, setLimit] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      const result = await new Audit().getSessions(currentPage, limit, {
        event: eventFilter || undefined,
        dateFrom: dateFromFilter || undefined,
        dateTo: dateToFilter || undefined,
        search: appliedSearch || undefined,
      });

      const response = getSessionsResponse(result);

      setSessions(response.docs || []);

      setPagination({
        totalDocs: Number(response.totalDocs) || 0,
        totalPages: Math.max(Number(response.totalPages) || 1, 1),
        page: Number(response.page) || currentPage,
        limit: Number(response.limit) || limit,
      });
    } catch (errorResponse) {
      console.error("loadSessions error:", errorResponse);

      setSessions([]);
      setPagination({ totalDocs: 0, totalPages: 1, page: 1, limit });

      setError(
        errorResponse?.msg ||
          "No fue posible cargar las sesiones. Intenta nuevamente."
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    appliedSearch,
    currentPage,
    dateFromFilter,
    dateToFilter,
    eventFilter,
    limit,
  ]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const firstVisibleItem = useMemo(() => {
    if (pagination.totalDocs === 0) {
      return 0;
    }

    return (pagination.page - 1) * pagination.limit + 1;
  }, [pagination]);

  const lastVisibleItem = useMemo(
    () => Math.min(pagination.page * pagination.limit, pagination.totalDocs),
    [pagination]
  );

  const hasFilters = Boolean(
    appliedSearch || eventFilter || dateFromFilter || dateToFilter
  );

  function handleSearchSubmit(event) {
    event.preventDefault();
    setCurrentPage(1);
    setAppliedSearch(searchValue.trim());
  }

  function handleClearFilters() {
    setSearchValue("");
    setAppliedSearch("");
    setEventFilter("");
    setDateFromFilter("");
    setDateToFilter("");
    setCurrentPage(1);
  }

  return (
    <section className={styles.AuditCard}>
      <div className={styles.FiltersBar}>
        <form className={styles.SearchForm} onSubmit={handleSearchSubmit}>
          <input
            type="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Buscar por usuario, correo o IP..."
            aria-label="Buscar por usuario, correo o IP"
          />

          <button type="submit" aria-label="Buscar">
            ⌕
          </button>
        </form>

        <label className={styles.FilterControl}>
          <span>Evento</span>

          <select
            value={eventFilter}
            onChange={(event) => {
              setEventFilter(event.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">Todos</option>

            {SESSION_EVENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div className={styles.DateRangeControl}>
          <span>Rango de fechas</span>

          <div className={styles.DateRangeInputs}>
            <input
              type="date"
              value={dateFromFilter}
              onChange={(event) => {
                setDateFromFilter(event.target.value);
                setCurrentPage(1);
              }}
              aria-label="Desde"
            />

            <span>—</span>

            <input
              type="date"
              value={dateToFilter}
              onChange={(event) => {
                setDateToFilter(event.target.value);
                setCurrentPage(1);
              }}
              aria-label="Hasta"
            />
          </div>
        </div>

        <button
          type="button"
          className={styles.RefreshButton}
          onClick={loadSessions}
          disabled={isLoading}
        >
          {isLoading ? "Actualizando..." : "Actualizar"}
        </button>

        {hasFilters && (
          <button
            type="button"
            className={styles.ClearFiltersButton}
            onClick={handleClearFilters}
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {error && (
        <div className={styles.ErrorMessage} role="alert">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className={styles.StateMessage}>Cargando sesiones...</div>
      ) : sessions.length === 0 ? (
        <div className={styles.EmptyState}>
          <p>
            {hasFilters
              ? "No se encontraron sesiones con los filtros seleccionados."
              : "Todavía no hay sesiones registradas."}
          </p>
        </div>
      ) : (
        <>
          <div className={styles.TableContainer}>
            <table className={`${styles.Table} ${styles.SessionsTable}`}>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Usuario</th>
                  <th>Correo</th>
                  <th>Evento</th>
                  <th>IP</th>
                  <th>Navegador</th>
                </tr>
              </thead>

              <tbody>
                {sessions.map((session) => (
                  <tr key={session._id}>
                    <td>
                      <span className={styles.DateCell}>
                        {formatDateTime(session.createdAt)}
                      </span>
                    </td>

                    <td>
                      <span
                        className={styles.UserValue}
                        title={getSessionUserName(session)}
                      >
                        {getSessionUserName(session)}
                      </span>
                    </td>

                    <td>
                      <span className={styles.UserValue} title={session.email}>
                        {session.email || "—"}
                      </span>
                    </td>

                    <td>
                      <span
                        className={getSessionBadgeClass(styles, session.event)}
                      >
                        {getSessionEventLabel(session.event)}
                      </span>
                    </td>

                    <td>
                      <span className={styles.UserValue}>
                        {describeIp(session.ip)}
                      </span>
                    </td>

                    <td>
                      <span
                        className={styles.UserValue}
                        title={session.userAgent}
                      >
                        {describeUserAgent(session.userAgent)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <footer className={styles.Pagination}>
            <span className={styles.TableInfo}>
              Mostrando {firstVisibleItem} a {lastVisibleItem} de{" "}
              {pagination.totalDocs} sesiones
            </span>

            <div className={styles.PageControls}>
              <button
                type="button"
                className={styles.PageButton}
                onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                disabled={currentPage === 1}
                aria-label="Página anterior"
              >
                ‹
              </button>

              <span className={styles.CurrentPage}>
                {pagination.page} de {pagination.totalPages}
              </span>

              <button
                type="button"
                className={styles.PageButton}
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.min(page + 1, pagination.totalPages)
                  )
                }
                disabled={currentPage === pagination.totalPages}
                aria-label="Página siguiente"
              >
                ›
              </button>
            </div>

            <label className={styles.LimitControl}>
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setCurrentPage(1);
                }}
              >
                {LIMIT_OPTIONS.map((optionLimit) => (
                  <option key={optionLimit} value={optionLimit}>
                    {optionLimit} por página
                  </option>
                ))}
              </select>
            </label>
          </footer>
        </>
      )}
    </section>
  );
}
